import { CircularProgress } from "@mui/material";
import { FlexCenterWrapper } from "../../../../../../../../../components/DefaultNoDataWrapper";
import Text from "../../../../../../../../../components/Text";
import { DEFAULT_KEY_LABEL_COLOR } from "../../../../../../../../../utils/utils";

/**
 * Props for the LoadingAndEmptyStateWrapper component.
 *
 * @interface LoadingAndEmptyStateWrapperProps
 * @property {boolean} loading - Indicates whether data is currently being loaded. When true, displays a loading spinner with "Loading..." text.
 * @property {boolean} isEmpty - Indicates whether the data is empty (no items to display). When true, displays the empty message.
 * @property {string} emptyMessage - The message to display when the data is empty. Should be user-friendly and descriptive.
 * @property {ReactNode} children - The content to render when data is loaded and not empty. This is the actual component content.
 *
 * @example
 * ```tsx
 * <LoadingAndEmptyStateWrapper
 *   loading={isLoading}
 *   isEmpty={data.length === 0}
 *   emptyMessage="No records found"
 * />
 * ```
 */
interface LoadingAndEmptyStateWrapperProps {
  /** Indicates whether data is currently being loaded */
  loading: boolean;
  /** Indicates whether the data is empty (no items to display) */
  isEmpty?: boolean;
  /** The message to display when the data is empty */
  emptyMessage?: string;
}

/**
 * A wrapper component that handles loading and empty states for data display components.
 *
 * This component provides a consistent way to handle three states:
 * 1. **Loading state**: Shows a loading spinner with "Loading..." text
 * 2. **Empty state**: Shows a centered empty message when no data is available
 * 3. **Content state**: Renders the children when data is loaded and available
 *
 * **Usage Notes:**
 * - The `loading` prop takes priority over `isEmpty`. If `loading` is true, the loading state will be shown regardless of `isEmpty`.
 * - Ensure `isEmpty` is only true when data has finished loading and is actually empty.
 * - The component uses `FlexCenterWrapper` for consistent centering of loading and empty states.
 * 
 
 *
 * @param {LoadingAndEmptyStateWrapperProps} props - The component props
 * @returns {JSX.Element} Renders loading spinner, empty message
 *
 * @component
 */
const LoadingAndEmptyStateWrapper = ({
  loading,
  isEmpty = false,
  emptyMessage = "No data available",
}: LoadingAndEmptyStateWrapperProps) => {
  if (loading) {
    return (
      <FlexCenterWrapper>
        <Text color={DEFAULT_KEY_LABEL_COLOR}>
          <span>Loading...</span>
          <CircularProgress size={12} sx={{ ml: 0.5 }} />
        </Text>
      </FlexCenterWrapper>
    );
  }

  if (isEmpty) {
    return (
      <FlexCenterWrapper>
        <Text color={DEFAULT_KEY_LABEL_COLOR}>{emptyMessage}</Text>
      </FlexCenterWrapper>
    );
  }
};

export default LoadingAndEmptyStateWrapper;
